/**
 * ESCHER FALLS — Scene / Renderer / Light / OrthographicCamera の生成と frustum 管理。
 * MODULE-CONTRACTS §4、PROJECTION-VERIFIED §4 準拠。
 *
 * このモジュールは **カメラの position / up / lookAt を一切触らない**。
 * それらは render/cameraRig.ts の専有物であり、ここが書き換えるのは
 * left / right / top / bottom / near / far（＝ frustum）だけである。
 */

import * as THREE from 'three';
import type { Bounds, Vec3 } from '../core/types';
import { ELEVATION_ANGLES, azimuthAngle, poseFrame } from '../core/projection';

/** 背景（紙）。CORE-SPEC §6。 */
// 背景は紙色。**着せ替えで変わる**ので PALETTE から引く（定数で持たない）。
import { PALETTE } from './materials';

/** frustum の余白（ワールド単位 = グリッド1マス）。 */
const DEFAULT_PADDING = 1.0;

/**
 * 近クリップ。正射影の深度は線形なので、`dist - sphereR ≫ near` である限り
 * 手前のパーツがクリップされることはない（PROJECTION-VERIFIED §4.1 で実測済み）。
 */
const NEAR = 0.1;

/** halfW / halfH がゼロになって除算が壊れないための下限。 */
const MIN_HALF = 1e-3;

const ORIGIN_BOUNDS: Bounds = {
  min: [0, 0, 0],
  max: [0, 0, 0],
  center: [0, 0, 0],
};

export interface CameraFrustum {
  /** 正射影の半幅（ワールド単位）。 */
  readonly halfW: number;
  /** 正射影の半高（ワールド単位）。 */
  readonly halfH: number;
  readonly near: number;
  readonly far: number;
  /** 注視点からカメラまでの距離。 */
  readonly dist: number;
  /** 注視点 = bounds.center。 */
  readonly center: Vec3;
}

export interface SceneBundle {
  readonly renderer: THREE.WebGLRenderer;
  readonly scene: THREE.Scene;
  /** 唯一のカメラ。cameraRig がこれを動かす。 */
  readonly camera: THREE.OrthographicCamera;
  readonly canvas: HTMLCanvasElement;
  /** ピースのメッシュを入れる親。レベル切替時はこの子だけを捨てる。 */
  readonly root: THREE.Group;
  /** 現在の frustum（読み取り専用。resize / setBounds で更新される）。 */
  readonly frustum: CameraFrustum;
  /** 地の色を差し替える。着せ替えのときに呼ぶ。 */
  setBackground(hex: number): void;
  /** レベル切替時に呼ぶ。frustum を再計算してカメラに反映する。 */
  setBounds(bounds: Bounds): void;
  /** canvas の CSS サイズに合わせて drawingBuffer と frustum を更新する。 */
  resize(): void;
  /** 1フレーム描画する。 */
  render(): void;
  /** root の子を全て取り除いて dispose する（レベル切替）。 */
  clearRoot(): void;
  dispose(): void;
}

/** bbox の外接球半径。 */
function boundingSphereRadius(bounds: Bounds): number {
  const dx = bounds.max[0] - bounds.min[0];
  const dy = bounds.max[1] - bounds.min[1];
  const dz = bounds.max[2] - bounds.min[2];
  return Math.hypot(dx, dy, dz) * 0.5;
}

/**
 * 注視点からカメラまでの距離。
 * `render/cameraRig.ts` もこれを使うこと（値がズレると near/far と食い違って初フレームで飛ぶ）。
 */
export function cameraDistance(bounds: Bounds): number {
  return boundingSphereRadius(bounds) * 2 + 50;
}

/**
 * bounds と aspect から frustum を算出する純関数。PROJECTION-VERIFIED §4 のコードそのもの。
 *
 * bbox の8隅を **4視点すべて**で投影し、必要な半幅・半高の最大値を取る。
 * 退化した bbox（1点・厚み0）でも動く。
 */
export function computeCameraFrustum(
  bounds: Bounds,
  aspect: number,
  padding: number = DEFAULT_PADDING,
): CameraFrustum {
  const center = new THREE.Vector3(bounds.center[0], bounds.center[1], bounds.center[2]);

  const corners: THREE.Vector3[] = [];
  for (const x of [bounds.min[0], bounds.max[0]]) {
    for (const y of [bounds.min[1], bounds.max[1]]) {
      for (const z of [bounds.min[2], bounds.max[2]]) {
        corners.push(new THREE.Vector3(x, y, z));
      }
    }
  }

  // **12姿勢すべて**で8隅を投影して最大値を取る（PROJECTION-9VIEW §3.9.4）。
  //
  // 旧実装は4視点だけを見ており、かつ `up.cross(zAxis)` を使っていた。
  // 真上視点では `up` と `zAxis` がどちらも (0,1,0) なので外積が**零ベクトル**になり
  // （NaN ではないので誰も気づかない）、真上が黙って最大値計算から脱落する。
  // 平たく広い bbox で 51.7%、厚み0の水平面で 58.8% frustum が小さすぎた。
  // poseFrame() の up を使えばこの穴は塞がる（dir ⊥ up が恒等的に成立する）。
  const viewDir = new THREE.Vector3();
  const upAxis = new THREE.Vector3();
  const rightAxis = new THREE.Vector3();
  const rel = new THREE.Vector3();

  let halfW = 0;
  let halfH = 0;
  for (let elevation = 0; elevation < 3; elevation += 1) {
    for (let azimuth = 0; azimuth < 4; azimuth += 1) {
      const frame = poseFrame(azimuthAngle(azimuth), ELEVATION_ANGLES[elevation]);
      // viewDir = -dir。right = normalize(cross(viewDir, up))、camUp は up そのもの。
      viewDir.set(-frame.dir[0], -frame.dir[1], -frame.dir[2]);
      upAxis.set(frame.up[0], frame.up[1], frame.up[2]);
      rightAxis.copy(viewDir).cross(upAxis).normalize();
      for (const c of corners) {
        rel.copy(c).sub(center);
        halfW = Math.max(halfW, Math.abs(rel.dot(rightAxis)));
        halfH = Math.max(halfH, Math.abs(rel.dot(upAxis)));
      }
    }
  }
  halfW = Math.max(halfW + padding, MIN_HALF);
  halfH = Math.max(halfH + padding, MIN_HALF);

  // frustum のアスペクトをキャンバスに一致させる（等角を歪ませないため）。
  const a = Number.isFinite(aspect) && aspect > 0 ? aspect : 1;
  if (halfW / halfH < a) halfW = halfH * a;
  else halfH = halfW / a;

  const sphereR = boundingSphereRadius(bounds);
  const dist = cameraDistance(bounds);

  return {
    halfW,
    halfH,
    near: NEAR,
    // 錯視のため「手前のパーツ」が絶対にクリップされないことを優先して思い切り広く取る。
    far: dist + sphereR * 2 + 50,
    dist,
    center: bounds.center,
  };
}

/** container（= #app）内の canvas#scene を使って一式を作る。 */
export function createScene(container: HTMLElement): SceneBundle {
  const found = container.querySelector<HTMLCanvasElement>('#scene');
  if (!found) {
    throw new Error('createScene: container の中に canvas#scene が見つからない');
  }
  // 巻き上げられる内部関数から参照するため、非 null の型で束ね直す。
  const canvas: HTMLCanvasElement = found;

  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

  const scene = new THREE.Scene();
  scene.background = new THREE.Color(PALETTE.paper);

  // CORE-SPEC §6: DirectionalLight 1灯 + AmbientLight。shadowMap は使わない。
  const key = new THREE.DirectionalLight(0xffffff, 1.6);
  key.position.set(3, 8, 5);
  key.name = 'keyLight';
  scene.add(key);
  const ambient = new THREE.AmbientLight(0xffffff, 0.55);
  ambient.name = 'ambientLight';
  scene.add(ambient);

  const root = new THREE.Group();
  root.name = 'root';
  scene.add(root);

  let bounds: Bounds = ORIGIN_BOUNDS;
  let frustum = computeCameraFrustum(bounds, currentAspect());

  const camera = new THREE.OrthographicCamera(
    -frustum.halfW,
    frustum.halfW,
    frustum.halfH,
    -frustum.halfH,
    frustum.near,
    frustum.far,
  );
  camera.up.set(0, 1, 0);
  applyFrustum();

  /** canvas の CSS サイズから aspect を得る。レイアウト前は 1 を返す。 */
  function currentAspect(): number {
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    if (w <= 0 || h <= 0) return 1;
    return w / h;
  }

  /** frustum をカメラに反映する。position / lookAt は触らない（cameraRig の領分）。 */
  function applyFrustum(): void {
    camera.left = -frustum.halfW;
    camera.right = frustum.halfW;
    camera.top = frustum.halfH;
    camera.bottom = -frustum.halfH;
    camera.near = frustum.near;
    camera.far = frustum.far;
    // camera.zoom は使わない（PROJECTION-VERIFIED §4.3）。
    camera.updateProjectionMatrix();
  }

  /**
   * 子オブジェクトの geometry を解放する。
   * **material は解放しない** — render/materials.ts の共有シングルトンであり、
   * ここで捨てると次のレベルで死んだマテリアルを使うことになる。
   */
  function disposeSubtree(obj: THREE.Object3D): void {
    obj.traverse((node) => {
      const withGeometry = node as { geometry?: THREE.BufferGeometry };
      withGeometry.geometry?.dispose();
    });
  }

  function clearRoot(): void {
    for (const child of [...root.children]) {
      root.remove(child);
      disposeSubtree(child);
    }
  }

  function setBounds(next: Bounds): void {
    bounds = next;
    frustum = computeCameraFrustum(bounds, currentAspect());
    applyFrustum();
  }

  function resize(): void {
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    if (w <= 0 || h <= 0) return; // レイアウト確定前に呼ばれた
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(w, h, false); // CSS サイズは触らない
    frustum = computeCameraFrustum(bounds, w / h);
    applyFrustum();
  }

  // 生成直後に一度合わせておく（レイアウト前なら何もしないので害はない）。
  // window の resize リスナはここで登録しない（main.ts の責務。MODULE-CONTRACTS §4.2）。
  resize();

  return {
    renderer,
    scene,
    camera,
    canvas,
    root,
    get frustum(): CameraFrustum {
      return frustum;
    },

    setBackground(hex: number): void {
      scene.background = new THREE.Color(hex);
    },
    setBounds,
    resize,

    render(): void {
      renderer.render(scene, camera);
    },

    clearRoot,

    dispose(): void {
      clearRoot();
      renderer.dispose();
    },
  };
}
